import { useState, useEffect } from 'react';
import { useAuth, canSeeInvoiceAmt, canExport, canRunSync } from '../context/AuthContext';

const S = {
  page: { padding: '32px 36px', fontFamily: "'Inter', sans-serif" },
  topbar: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 600, color: 'var(--text1)', margin: 0 },
  sub: { fontSize: 14, color: 'var(--text3)', marginTop: 4 },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 13, color: 'var(--text2)', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  btnPrimary: { background: 'var(--purple)', color: '#fff', border: '1px solid var(--purple)' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 },
  stat: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px' },
  statLabel: { fontSize: 12, color: 'var(--text3)', margin: '0 0 6px', fontWeight: 500 },
  statVal: { fontSize: 26, fontWeight: 600, color: 'var(--text1)', margin: 0 },
  tableWrap: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' },
  td: { padding: '13px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text2)', verticalAlign: 'middle' },
  input: { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 13, color: 'var(--text2)', fontFamily: "'Inter', sans-serif" },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
};

const STATUS_STYLE = {
  success: { background: 'var(--gbg)', color: 'var(--green)' },
  failed:  { background: 'var(--rbg)', color: 'var(--red)' },
  pending: { background: 'var(--obg)', color: 'var(--orange)' },
  skipped: { background: 'var(--bg3)', color: 'var(--text3)' },
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [summary, setSummary] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  useEffect(() => {
    fetch('/api/invoices/log')
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setLogs(list);
        setLoading(false);
        // Monthly summary straight from the database (always accurate).
        // Falls back to computing from loaded rows until the route is deployed.
        return fetch('/api/invoices/summary')
          .then(r => (r.ok ? r.json() : Promise.reject()))
          .then(s => { if (Array.isArray(s) && s.length) { setSummary(s); } else { throw new Error('empty'); } })
          .catch(() => {
            const map = {};
            list.forEach(l => {
              const p = l.period || 'Unknown';
              if (!map[p]) map[p] = { period: p, total_count: 0, success_count: 0, failed_count: 0, total_amount: 0, latest: l.synced_at };
              map[p].total_count += 1;
              if (l.status === 'success') { map[p].success_count += 1; map[p].total_amount += (parseFloat(l.amount) || 0); }
              if (l.status === 'failed') map[p].failed_count += 1;
              if (l.synced_at > map[p].latest) map[p].latest = l.synced_at;
            });
            setSummary(Object.values(map).sort((a, b) => new Date(b.latest) - new Date(a.latest)));
          });
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = logs.filter(l => {
    const matchSearch = l.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
                        l.lc_invoice_id?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || l.status === statusFilter;
    const matchPeriod = periodFilter === 'all' || l.period === periodFilter;
    return matchSearch && matchStatus && matchPeriod;
  });

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  // Stat cards come from the database summary (accurate beyond the 500 loaded rows)
  const scoped = periodFilter === 'all' ? summary : summary.filter(s => s.period === periodFilter);
  const stats = {
    total:    scoped.reduce((a, s) => a + (parseInt(s.total_count) || 0), 0),
    success:  scoped.reduce((a, s) => a + (parseInt(s.success_count) || 0), 0),
    failed:   scoped.reduce((a, s) => a + (parseInt(s.failed_count) || 0), 0),
    totalAmt: scoped.reduce((a, s) => a + (parseFloat(s.total_amount) || 0), 0),
  };

  const handleSync = async () => {
    setSyncing(true);
    await fetch('/api/sync/run', { method: 'POST' });
    const d = await fetch('/api/invoices/log').then(r => r.json());
    setLogs(Array.isArray(d) ? d : []);
    setSyncing(false);
  };

  const handleExport = () => {
    const params = new URLSearchParams({ status: statusFilter, search });
    window.open(`/api/invoices/export?${params}`, '_blank');
  };

  if (loading) return <div style={{ ...S.page, color: 'var(--text3)' }}>Loading invoice log...</div>;

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div>
          <h1 style={S.title}>Invoices</h1>
          <p style={S.sub}>Sync log — every invoice pushed from LeaderCloud to QuickBooks</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canRunSync(user) && (
            <button style={{ ...S.btn, ...S.btnPrimary }} onClick={handleSync} disabled={syncing}>
              {syncing ? '⟳ Syncing...' : '↻ Run sync now'}
            </button>
          )}
          {canExport(user) && (
            <button style={S.btn} onClick={handleExport}>⤓ Export CSV</button>
          )}
        </div>
      </div>

      <div style={S.stats}>
        <div style={S.stat}>
          <p style={S.statLabel}>Total synced</p>
          <p style={S.statVal}>{stats.total.toLocaleString()}</p>
        </div>
        <div style={S.stat}>
          <p style={S.statLabel}>Successful</p>
          <p style={{ ...S.statVal, color: 'var(--green)' }}>{stats.success.toLocaleString()}</p>
        </div>
        <div style={S.stat}>
          <p style={S.statLabel}>Failed</p>
          <p style={{ ...S.statVal, color: stats.failed > 0 ? 'var(--red)' : 'var(--text1)' }}>{stats.failed}</p>
        </div>
        {canSeeInvoiceAmt(user) ? (
          <div style={S.stat}>
            <p style={S.statLabel}>Total invoiced</p>
            <p style={{ ...S.statVal, fontSize: 22 }}>${stats.totalAmt.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        ) : (
          <div style={S.stat}>
            <p style={S.statLabel}>Success rate</p>
            <p style={{ ...S.statVal, color: 'var(--green)' }}>
              {stats.total > 0 ? Math.round(stats.success / stats.total * 100) : 0}%
            </p>
          </div>
        )}
      </div>

      {/* Monthly breakdown — visible on All months; click a row to drill in */}
      {periodFilter === 'all' && summary.length > 1 && (
        <div style={{ ...S.tableWrap, marginBottom: 18 }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Month</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Invoices</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Failed</th>
                {canSeeInvoiceAmt(user) && <th style={{ ...S.th, textAlign: 'right' }}>Total invoiced</th>}
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {summary.map(s => (
                <tr key={s.period} onClick={() => { setPeriodFilter(s.period); setPage(1); }} style={{ cursor: 'pointer' }}>
                  <td style={{ ...S.td, fontWeight: 600, color: 'var(--text1)' }}>{s.period || '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{parseInt(s.success_count) || 0}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: (parseInt(s.failed_count) || 0) > 0 ? 'var(--red)' : 'var(--text3)' }}>{parseInt(s.failed_count) || 0}</td>
                  {canSeeInvoiceAmt(user) && (
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: 'var(--text1)' }}>
                      ${(parseFloat(s.total_amount) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  )}
                  <td style={{ ...S.td, textAlign: 'right', color: 'var(--purple-bright)', fontSize: 12 }}>View →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input style={{ ...S.input, width: 260 }} placeholder="Search customer or invoice ID..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select style={{ ...S.input, width: 160 }} value={periodFilter} onChange={e => { setPeriodFilter(e.target.value); setPage(1); }}>
          <option value="all">All months</option>
          {summary.map(s => (
            <option key={s.period} value={s.period}>{s.period}</option>
          ))}
        </select>
        <select style={{ ...S.input, width: 150 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">All statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Customer</th>
              <th style={S.th}>LC Invoice ID</th>
              <th style={S.th}>QB Invoice ID</th>
              <th style={S.th}>Period</th>
              {canSeeInvoiceAmt(user) && <th style={{ ...S.th, textAlign: 'right' }}>Amount</th>}
              <th style={S.th}>Status</th>
              <th style={S.th}>Synced at</th>
              <th style={S.th}>Note</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((log, i) => (
              <tr key={i} style={log.status === 'failed' ? { background: 'var(--rbg)' } : {}}>
                <td style={S.td}>
                  <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{log.customer_name || '—'}</div>
                </td>
                <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)' }}>
                  {log.lc_invoice_id || '—'}
                </td>
                <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)' }}>
                  {log.qb_invoice_id || '—'}
                </td>
                <td style={{ ...S.td, color: 'var(--text2)' }}>
                  {log.period || (log.synced_at ? new Date(log.synced_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : '—')}
                </td>
                {canSeeInvoiceAmt(user) && (
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>
                    {log.amount ? `$${parseFloat(log.amount).toLocaleString('en-AU', { minimumFractionDigits: 2 })}` : '—'}
                  </td>
                )}
                <td style={S.td}>
                  <span style={{ ...S.badge, ...(STATUS_STYLE[log.status] || STATUS_STYLE.pending) }}>
                    {log.status || 'pending'}
                  </span>
                </td>
                <td style={{ ...S.td, color: 'var(--text3)', fontSize: 12 }}>
                  {log.synced_at ? new Date(log.synced_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                </td>
                <td style={{ ...S.td, color: log.error_message ? 'var(--red)' : 'var(--text3)', fontSize: 12, maxWidth: 200 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.error_message || log.note || '—'}
                  </div>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: 'var(--text3)', padding: 40 }}>No invoice records found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length} records
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={S.btn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
            <span style={{ padding: '8px 14px', fontSize: 13, color: 'var(--text2)' }}>Page {page} of {totalPages}</span>
            <button style={S.btn} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}
